import { useLocation } from "wouter";
import { darkTheme, keyframes, styled } from "~/ui/style/stitches.config";
import { Button } from "~/ui/components/Button";
import { mp4Url } from "~/utils/imageurl";
import { isIOSNativeApp } from "~/utils/deviceDetection";

const fadeIn = keyframes({
  "0%": { opacity: 0 },
  "100%": { opacity: 1 },
});

export type CardsView = "guidebook" | "decks";

interface CardsHeaderProps {
  activeView: CardsView;
}

export const CardsHeader = ({ activeView }: CardsHeaderProps) => {
  const [, setLocation] = useLocation();

  const activeIndex = activeView === "guidebook" ? 0 : 1;

  const handleGuidebookClick = () => {
    if (activeView !== "guidebook") {
      setLocation("/guidebook");
    }
  };

  const handleDecksClick = () => {
    if (activeView !== "decks") {
      setLocation("/decks");
    }
  };

  return (
    <StyledCardsHeader view={activeView} isIOSNative={isIOSNativeApp()}>
      <div className="toggle-container">
        <div
          className="background-pill"
          style={{
            transform: `translateX(${activeIndex * 100}%)`,
          }}
        />
        <button
          className={`toggle-btn ${activeView === "guidebook" ? "active" : ""}`}
          onClick={handleGuidebookClick}
        >
          Guidebook
        </button>
        <button
          className={`toggle-btn ${activeView === "decks" ? "active" : ""}`}
          onClick={handleDecksClick}
        >
          Decks
        </button>
      </div>

      {activeView === "guidebook" ? (
        <div className="header-content" key="guidebook">
          <h1 className="title">Meanings</h1>
          <p className="subtitle">
          Breakdowns of all 78 tarot cards as a spark for your own interpretations. By Moonlight's founder <a href="https://app.moonlight.world/profile/danielle" target="_blank" rel="noopener noreferrer">Danielle</a>, a tarot reader of 16 years. Includes both upright and reversed meanings.
          </p>
        </div>
      ) : (
        <div className="decks-content" key="decks">
          <div className="decks-text">
            <h1 className="title">Digital decks</h1>
            <p>
              Carry interactive digital editions of beloved tarot & oracle decks
              in your Moonlight rooms, for solo or collaborative rituals. Each
              deck comes with its own mini guidebook.
            </p>
            {!isIOSNativeApp() && (
              <Button
                style={{
                  padding: "14px 20px",
                  marginTop: "16px",
                  background: "rgb(237, 194, 154)",
                }}
                onClick={() => {
                  window.open(
                    "https://blog.moonlight.world/about-our-digital-tarot-decks/",
                    "_blank"
                  );
                }}
              >
                About the artists
              </Button>
            )}
          </div>
          {!isIOSNativeApp() && (
            <div className="decks-video">
              <video
                autoPlay
                loop
                muted
                playsInline
                src={mp4Url("/moonlight-decks-demo.mp4", "medium")}
              />
            </div>
          )}
        </div>
      )}
    </StyledCardsHeader>
  );
};

const StyledCardsHeader = styled("div", {
  backgroundColor: "rgba(236, 236, 236, 1)",
  border: "1px solid black",
  width: "calc(90% - $navWidth)",
  margin: "60px auto 34px auto",
  marginLeft: "calc($navWidth + 5%)",
  borderRadius: "33px",
  padding: "26px 80px 40px 80px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  textAlign: "center",
  [`.${darkTheme.className} &`]: {
    backgroundColor: "$appBg",
    border: "1px solid rgba(255, 255, 255, 0.6)",
  },

  "@mobile": {
    width: "calc(100% - 32px)",
    margin: "70px 16px 24px 16px",
    marginLeft: "16px",
    borderRadius: "30px",
    padding: "24px 30px 30px 30px",
    boxSizing: "border-box",
    textAlign: "left",
  },

  "& .toggle-container": {
    display: "flex",
    position: "relative",
    backgroundColor: "rgba(0, 0, 0, 0.05)",
    borderRadius: "50px",
    padding: "4px",
    border: "1px solid rgba(0, 0, 0, 0.2)",
    marginBottom: "20px",
    WebkitTapHighlightColor: "transparent",
    userSelect: "none",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "#555555",
      border: "1px solid rgba(255, 255, 255, 0.3)",
    },
  },

  "& .background-pill": {
    position: "absolute",
    top: "4px",
    left: "4px",
    width: "100px",
    height: "calc(100% - 8px)",
    backgroundColor: "white",
    borderRadius: "50px",
    transition: "transform 0.25s ease",
    [`.${darkTheme.className} &`]: {
      backgroundColor: "black",
    },
  },

  "& .toggle-btn": {
    position: "relative",
    zIndex: 1,
    padding: "8px 0",
    width: "100px",
    fontSize: "13px",
    fontWeight: "400",
    fontFamily: "inherit",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    transition: "color 0.2s ease, font-weight 0.2s ease",
    color: "rgba(0, 0, 0, 0.9)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    WebkitTapHighlightColor: "transparent",
    [`.${darkTheme.className} &`]: {
      color: "rgba(255, 255, 255, 0.9)",
    },

    "&:hover:not(.active)": {
      color: "rgba(0, 0, 0, 0.7)",
      [`.${darkTheme.className} &`]: {
        color: "rgba(255, 255, 255, 0.7)",
      },
    },

    "&.active": {
      fontWeight: "500",
      color: "black",
      [`.${darkTheme.className} &`]: {
        color: "white",
      },
    },

    "@mobile": {
      padding: "8px 20px",
      fontSize: "13px",
    },
  },

  "& .title": {
    fontFamily: "GorgeousBaby",
    fontWeight: "200",
    fontSize: "44px",
    lineHeight: 1,
    margin: "8px 0px",
    transition: "opacity 0.2s ease",
    [`.${darkTheme.className} &`]: {
      color: "$text",
    },
    "@mobile": {
      fontSize: "46px",
    },
  },

  "& .subtitle": {
    fontSize: "14px",
    lineHeight: "1.4",
    margin: "10px 10px 10px 0px",
    maxWidth: "430px",
    transition: "opacity 0.2s ease",
    [`.${darkTheme.className} &`]: {
      color: "$text",
    },
  },

  "& .header-content": {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    animation: `${fadeIn} 0.3s ease`,

    "@mobile": {
      alignItems: "flex-start",
      width: "100%",
    },
  },

  "& .decks-content": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    gap: "60px",
    marginTop: "10px",
    animation: `${fadeIn} 0.3s ease`,

    "@bp900": {
      flexDirection: "column",
      gap: "24px",
      alignItems: "stretch",
    },
  },

  "& .decks-text": {
    flex: "1",
    maxWidth: "400px",
    minWidth: "200px",
    textAlign: "left",

    "& p": {
      fontSize: "14px",
      lineHeight: "1.4",
      margin: "20px 8px 10px 0px",
      [`.${darkTheme.className} &`]: {
        color: "$text",
      },
    },

    "@bp900": {
      maxWidth: "100%",
      minWidth: "auto",
      textAlign: "left",
    },
  },

  "& .decks-video": {
    flex: "1 1 auto",
    maxWidth: "350px",

    "& video": {
      width: "100%",
      borderRadius: "8px",
      border: "1px solid rgba(0, 0, 0, 0.8)",
      [`.${darkTheme.className} &`]: {
        border: "1px solid rgba(255, 255, 255, 0.3)",
      },
    },

    "@bp900": {
      width: "80%",
      maxWidth: "350px",
    },
  },

  variants: {
    view: {
      guidebook: {},
      decks: {},
    },
    isIOSNative: {
      true: {
        "@notmobile": {
          width: "90%",
          marginLeft: "auto",
          marginRight: "auto",
        },
      },
    },
  },
});

